import { Modal, message } from "antd";
import { extractNonEmptyFormEntries } from "common/utils";
import { FormWizard } from "components/formWizard";
import { useLazyFetchAccountsQuery } from "features/accountsTable/agencyPortal/accountsTableAPI";
import { useCreateBulkActionMutation } from "features/bulkActions/bulkActionsAPI";
import { BULK_ACTION_MAX_RECORDS } from "common/constants";
import { splitRangeDatePickerValue } from "features/bulkActions/utils";
import { AccountFiltersStep } from "features/bulkActions/wizardSteps/accountFiltersStep";
import AccountOperationsStep from "features/bulkActions/wizardSteps/accountOperationsStep";
import AccountsPreviewStep from "features/bulkActions/wizardSteps/accountsPreviewStep";
import FinishStep from "features/bulkActions/wizardSteps/finishStep";
import moment from "moment-timezone";
import { useState } from "react";

const ACCOUNT_THRESHOLD_EXEMPT_OPS = new Set(["accountSkipTrace"]);

function areAllAccountOpsExempt(operations) {
  // Boolean checkboxes (e.g., delete) leave `false` behind after a toggle on/off,
  // which slips past extractNonEmptyFormEntries. Treat those as not-selected here.
  const opKeys = Object.entries(operations ?? {})
    .filter(([, value]) => value !== false)
    .map(([key]) => key);
  return opKeys.length > 0 && opKeys.every((op) => ACCOUNT_THRESHOLD_EXEMPT_OPS.has(op));
}

export function preTransformDateAndArrayFilters(values) {
  // Handle all array fields. If the array is empty, set it to null.
  // If the array is empty, it means we no longer want to filter by that field.
  // E.g if Debtor states is [], it means they selected some Debtor States, then removed them, so we no longer want to filter by Debtor states.
  Object.keys(values.filters).forEach((key) => {
    if (Array.isArray(values.filters[key]) && values.filters[key].length === 0) {
      values.filters[key] = null;
    }
  });

  // // Handle all date fields
  if (values?.filters?.followUpDate) {
    [values.filters.followUpStartDate, values.filters.followUpEndDate] = splitRangeDatePickerValue(
      values.filters.followUpDate,
    );
  }

  if (values?.filters?.excludeFollowUpDate) {
    [values.filters.excludeFollowUpStartDate, values.filters.excludeFollowUpEndDate] =
      splitRangeDatePickerValue(values.filters.excludeFollowUpDate);
  }

  if (values?.filters?.uploadDate) {
    [values.filters.uploadDateStart, values.filters.uploadDateEnd] = splitRangeDatePickerValue(
      values.filters.uploadDate,
    );
  }

  if (values?.filters?.excludeUploadDate) {
    [values.filters.excludeUploadDateStart, values.filters.excludeUploadDateEnd] =
      splitRangeDatePickerValue(values.filters.excludeUploadDate);
  }

  if (values?.filters?.includeDateOfService) {
    [values.filters.includeDateOfServiceStart, values.filters.includeDateOfServiceEnd] =
      splitRangeDatePickerValue(values.filters.includeDateOfService);
  }

  if (values?.filters?.excludeDateOfService) {
    [values.filters.excludeDateOfServiceStart, values.filters.excludeDateOfServiceEnd] =
      splitRangeDatePickerValue(values.filters.excludeDateOfService);
  }

  if (values?.filters?.includeTurnoverDate) {
    [values.filters.includeTurnoverDateStart, values.filters.includeTurnoverDateEnd] =
      splitRangeDatePickerValue(values.filters.includeTurnoverDate);
  }

  if (values?.filters?.excludeTurnoverDate) {
    [values.filters.excludeTurnoverDateStart, values.filters.excludeTurnoverDateEnd] =
      splitRangeDatePickerValue(values.filters.excludeTurnoverDate);
  }

  if (values?.filters?.includeOpenDate) {
    [values.filters.includeOpenDateStart, values.filters.includeOpenDateEnd] =
      splitRangeDatePickerValue(values.filters.includeOpenDate);
  }

  if (values?.filters?.excludeOpenDate) {
    [values.filters.excludeOpenDateStart, values.filters.excludeOpenDateEnd] =
      splitRangeDatePickerValue(values.filters.excludeOpenDate);
  }

  if (values?.filters?.includeDateOfFirstDelinquency) {
    [
      values.filters.includeDateOfFirstDelinquencyStart,
      values.filters.includeDateOfFirstDelinquencyEnd,
    ] = splitRangeDatePickerValue(values.filters.includeDateOfFirstDelinquency);
  }

  if (values?.filters?.excludeDateOfFirstDelinquency) {
    [
      values.filters.excludeDateOfFirstDelinquencyStart,
      values.filters.excludeDateOfFirstDelinquencyEnd,
    ] = splitRangeDatePickerValue(values.filters.excludeDateOfFirstDelinquency);
  }

  if (values?.filters?.includeItemizationDate) {
    [values.filters.includeItemizationDateStart, values.filters.includeItemizationDateEnd] =
      splitRangeDatePickerValue(values.filters.includeItemizationDate);
  }

  if (values?.filters?.excludeItemizationDate) {
    [values.filters.excludeItemizationDateStart, values.filters.excludeItemizationDateEnd] =
      splitRangeDatePickerValue(values.filters.excludeItemizationDate);
  }

  if (values?.filters?.includeChargeOffDate) {
    [values.filters.includeChargeOffDateStart, values.filters.includeChargeOffDateEnd] =
      splitRangeDatePickerValue(values.filters.includeChargeOffDate);
  }

  if (values?.filters?.excludeChargeOffDate) {
    [values.filters.excludeChargeOffDateStart, values.filters.excludeChargeOffDateEnd] =
      splitRangeDatePickerValue(values.filters.excludeChargeOffDate);
  }

  if (values?.filters?.includeInterestStartDate) {
    [values.filters.includeInterestStartDateStart, values.filters.includeInterestStartDateEnd] =
      splitRangeDatePickerValue(values.filters.includeInterestStartDate);
  }

  if (values?.filters?.excludeInterestStartDate) {
    [values.filters.excludeInterestStartDateStart, values.filters.excludeInterestStartDateEnd] =
      splitRangeDatePickerValue(values.filters.excludeInterestStartDate);
  }

  if (values?.filters?.includeInterestEndDate) {
    [values.filters.includeInterestEndDateStart, values.filters.includeInterestEndDateEnd] =
      splitRangeDatePickerValue(values.filters.includeInterestEndDate);
  }

  if (values?.filters?.excludeInterestEndDate) {
    [values.filters.excludeInterestEndDateStart, values.filters.excludeInterestEndDateEnd] =
      splitRangeDatePickerValue(values.filters.excludeInterestEndDate);
  }

  if (values?.filters?.includeLastPaymentDate) {
    [values.filters.includeLastPaymentDateStart, values.filters.includeLastPaymentDateEnd] =
      splitRangeDatePickerValue(values.filters.includeLastPaymentDate);
  }

  if (values?.filters?.excludeLastPaymentDate) {
    [values.filters.excludeLastPaymentDateStart, values.filters.excludeLastPaymentDateEnd] =
      splitRangeDatePickerValue(values.filters.excludeLastPaymentDate);
  }

  if (values?.filters?.includeLastCallDate) {
    [values.filters.includeLastCallDateStart, values.filters.includeLastCallDateEnd] =
      splitRangeDatePickerValue(values.filters.includeLastCallDate);
  }

  if (values?.filters?.excludeLastCallDate) {
    [values.filters.excludeLastCallDateStart, values.filters.excludeLastCallDateEnd] =
      splitRangeDatePickerValue(values.filters.excludeLastCallDate);
  }

  if (values?.filters?.includeLastLetterDate) {
    [values.filters.includeLastLetterDateStart, values.filters.includeLastLetterDateEnd] =
      splitRangeDatePickerValue(values.filters.includeLastLetterDate);
  }

  if (values?.filters?.excludeLastLetterDate) {
    [values.filters.excludeLastLetterDateStart, values.filters.excludeLastLetterDateEnd] =
      splitRangeDatePickerValue(values.filters.excludeLastLetterDate);
  }

  if (values?.filters?.includeLastEmailDate) {
    [values.filters.includeLastEmailDateStart, values.filters.includeLastEmailDateEnd] =
      splitRangeDatePickerValue(values.filters.includeLastEmailDate);
  }

  if (values?.filters?.excludeLastEmailDate) {
    [values.filters.excludeLastEmailDateStart, values.filters.excludeLastEmailDateEnd] =
      splitRangeDatePickerValue(values.filters.excludeLastEmailDate);
  }

  return values;
}

function AccountBulkActionsModal({ title, open: bulkActionRecord, onOk, onCancel }) {
  const [createBulkAction, { isLoading }] = useCreateBulkActionMutation();
  const [fetchAccounts, { data: accounts, isFetching: isAccountsFetching }] =
    useLazyFetchAccountsQuery();
  const [hideParentModal, setHideParentModal] = useState(false);
  const [chosenOperations, setChosenOperations] = useState({});

  const accountsCount = accounts?.meta?.resultCount ?? 0;
  const hasChosenOps = Object.keys(chosenOperations).length > 0;
  const isOverThresholdWithNonExempt =
    hasChosenOps &&
    accountsCount > BULK_ACTION_MAX_RECORDS &&
    !areAllAccountOpsExempt(chosenOperations);

  const onFiltersDone = async (values) => {
    const hasAnyValueSet = Object.values(extractNonEmptyFormEntries(values.filters)).length > 0;

    if (!hasAnyValueSet) {
      return { error: { message: "Please select at least one filter." } };
    }

    // Split date range values into start and end dates
    const transformedValues = preTransformDateAndArrayFilters(values);

    return fetchAccounts({
      ...transformedValues,
      includeResultCount: true, // Include the results count in the response
    });
  };

  const onOperationsDone = async (values, { step0 }) => {
    const cleanedOperations = extractNonEmptyFormEntries(values.operations);
    const hasAnyValueSet = Object.values(cleanedOperations).length > 0;

    if (!hasAnyValueSet) {
      return { error: { message: "Please select at least one operation." } };
    }

    setChosenOperations(cleanedOperations);

    const payload = {
      ...(await step0.validateFields()),
      ...values,
    };

    return payload;
  };

  const onFinish = async (_, { step0, step2 }) => {
    let payload = {
      ...(await step0.validateFields()),
      ...(await step2.validateFields()),
    };

    // Split date range values into start and end dates
    payload = preTransformDateAndArrayFilters(payload);

    // Remove entries that contain all empty values, plus boolean checkboxes that
    // were toggled on then off (antd retains `false` rather than `undefined`,
    // which would otherwise reach the backend as a phantom op).
    payload.operations = Object.fromEntries(
      Object.entries(extractNonEmptyFormEntries(payload.operations)).filter(
        ([, value]) => value !== false,
      ),
    );

    // followUpDaysFromToday is a UI-only field — compute the date and apply it to setFollowUpDate
    if (payload.operations.followUpDaysFromToday) {
      payload.operations.setFollowUpDate = {
        followUpDate: moment()
          .add(payload.operations.followUpDaysFromToday, "days")
          .format("YYYY-MM-DD"),
      };
    }
    delete payload.operations.followUpDaysFromToday;

    const result = await createBulkAction(payload);
    if ("data" in result) {
      message.success(`Bulk action job created successfully!`);
    }
    if ("error" in result) {
      message.error("Failed to create bulk action job.");
    }
    return result;
  };

  const onDone = () => {
    onOk();
  };

  const steps = [
    {
      title: "Accounts",
      form: <AccountFiltersStep />,
      onNext: async (data) => onFiltersDone(data),
      wrapperProps: {
        style: {
          maxWidth: "100%",
          width: "100%",
        },
      },
    },
    {
      title: "Preview",
      form: (
        <AccountsPreviewStep
          previewAccounts={accounts?.results ?? []}
          previewAccountsTotalCount={accountsCount}
        />
      ),
      wrapperProps: {
        style: {
          width: "100%",
          maxWidth: "100%",
        },
      },
      nextButtonProps: {
        disabled: isAccountsFetching,
        loading: isAccountsFetching,
      },
    },
    {
      title: "Operations",
      form: <AccountOperationsStep setHideParentModal={setHideParentModal} />,
      onNext: onOperationsDone,
    },
    {
      title: "Finish",
      form: <FinishStep isOverThresholdWithNonExempt={isOverThresholdWithNonExempt} />,
      wrapperProps: {
        style: {
          margin: "0 auto",
        },
      },
      doneButtonText: "Confirm",
      doneButtonProps: { disabled: isOverThresholdWithNonExempt },
      onNext: onFinish,
    },
    // {
    //   title: "Select Date",
    //   subTitle: "(Recurring Schedule Coming Soon)",
    //   form: (
    //     <StyledCard>
    //       <StyledHeader>Select a date and time to schedule the job:</StyledHeader>
    //       <Form.Item
    //         rules={[{ required: true, message: "This field is required." }]}
    //         name="oneOffScheduledAt"
    //       >
    //         <DatePicker disabledDate={disabledDate} showSecond={false} showTime />
    //       </Form.Item>
    //     </StyledCard>
    //   ),
    // },
    // {
    //   title: "Job Identifier",
    //   form: (
    //     <Form.Item rules={[{ required: true, message: "This field is required." }]} name="name">
    //       <StyledCard>
    //         <StyledHeader>Enter job name:</StyledHeader>
    //         <Input placeholder="Enter name..." />
    //       </StyledCard>
    //     </Form.Item>
    //   ),
    // },
  ];

  const initialValues = {
    type: "account",
    cadence: "one-off",
    schedule: "asap",
    every: {
      time: moment().format("hh:mm A"),
    },
  };

  return (
    <Modal
      maskClosable={false}
      title={title}
      open={bulkActionRecord && !hideParentModal}
      onCancel={onCancel}
      confirmLoading={isLoading}
      footer={null}
      width={800}
    >
      <FormWizard steps={steps} onCancel={onCancel} onDone={onDone} initialValues={initialValues} />
    </Modal>
  );
}

export default AccountBulkActionsModal;
